import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Customer, CustomerStatus } from '@/model/customer.model';
import { PaginatedResponse } from '@/model/paginated-response.model';
import { config } from '../config';

@Injectable({
  providedIn: 'root',
})
export class CustomersService {

  private readonly apiBaseUrl = config.apiUrl;
  private readonly customersUrl = `${this.apiBaseUrl}/customers`;

  constructor(private http: HttpClient) {}

  /** 🔐 توکن */
  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('token');
    return new HttpHeaders({
      Authorization: token ? `Bearer ${token}` : '',
    });
  }

  /** 📌 دریافت مشتریان (پایگاه داده شماره‌ها) */
  getCustomers(page = 1, limit = 20) {
    return this.http.get<PaginatedResponse<Customer>>(
      `${this.customersUrl}?page=${page}&limit=${limit}`,
      { headers: this.getAuthHeaders() }
    );
  }

  /** 📌 دریافت مشتریان با یک وضعیت خاص */
  getCustomersByStatus(status: string, page = 1, limit = 20) {
    return this.http.get<PaginatedResponse<Customer>>(
      `${this.customersUrl}/status/${status}?page=${page}&limit=${limit}`,
      { headers: this.getAuthHeaders() }
    );
  }

  /** 📌 مخصوص "پاسخگو نبود" */
  getUnreachable(page = 1, limit = 20) {
    return this.getCustomersByStatus('unreachable', page, limit);
  }

  /** 📌 مخصوص "غیرفعال (ویپ و پاد نمی‌فروشد)" */
  getDeactive(page = 1, limit = 20) {
    return this.getCustomersByStatus('rejected', page, limit);
  }

  /** 📌 مخصوص مشتریان فعال */
  getActive(page = 1, limit = 20) {
    return this.getCustomersByStatus('active', page, limit);
  }

  /** ✏ ویرایش مشتری */
  updateCustomer(id: string, payload: Partial<Customer>): Observable<Customer> {
    return this.http.patch<Customer>(
      `${this.customersUrl}/${id}`,
      payload,
      { headers: this.getAuthHeaders() }
    );
  }

  /** 🔄 تغییر وضعیت مشتری */
  updateStatus(id: string, status: CustomerStatus): Observable<any> {
    return this.http.post(
      `${this.customersUrl}/${id}/status`,
      { status },
      { headers: this.getAuthHeaders() }
    );
  }

  /** 📝 اضافه کردن یادداشت */
  addLog(id: string, note: string): Observable<any> {
    return this.http.post(
      `${this.customersUrl}/${id}/log`,
      { note },
      { headers: this.getAuthHeaders() }
    );
  }

  // ✅ مشتریان active-selling
  // NEW: filters: { isSatisfied?: boolean, businessCustomerType?: string }
  getActiveSelling(
    page = 1,
    limit = 20,
    filters?: { isSatisfied?: boolean; businessCustomerType?: string }
  ) {
    let params: any = { page, limit };

    if (filters?.isSatisfied !== undefined && filters?.isSatisfied !== null) {
      params.isSatisfied = String(filters.isSatisfied); // "false" / "true"
    }

    if (filters?.businessCustomerType) {
      params.businessCustomerType = String(filters.businessCustomerType); // "VIP" | "NORMAL" | "WHOLESALE"
    }

    return this.http.get(
      `${this.customersUrl}/active-selling`,
      {
        headers: this.getAuthHeaders(),
        params,
      }
    );
  }

  // ✅ داشبورد: آمار روزانه active-selling (پیشفرض ۷ روز)
  // GET /customers/dashboard/active-selling-daily?days=7
  getActiveSellingDailyStats(days = 7) {
    const params = new HttpParams().set('days', String(days));
    return this.http.get<any[]>(
      `${this.customersUrl}/dashboard/active-selling-daily`,
      {
        headers: this.getAuthHeaders(),
        params,
      }
    );
  }

  // -------------------------
  // 🔒 NEW: LOCK / UNLOCK
  // -------------------------
  lockCustomer(id: string) {
    return this.http.post(
      `${this.customersUrl}/${id}/lock`,
      {},
      { headers: this.getAuthHeaders() }
    );
  }

  unlockCustomer(id: string) {
    return this.http.post(
      `${this.customersUrl}/${id}/unlock`,
      {},
      { headers: this.getAuthHeaders() }
    );
  }

getActiveSellingSummaryByDate(date: string) {
  return this.http.get<any[]>(
    `${this.customersUrl}/active-selling/summary`,
    {
      headers: this.getAuthHeaders(),
      params: { date },
    }
  );
}

getActiveSellingSummaryByRange(from: string, to: string) {
  return this.http.get<any[]>(
    `${this.customersUrl}/active-selling/summary-range`,
    {
      headers: this.getAuthHeaders(),
      params: { from, to },
    }
  );
}

getUnreachableSummaryByRange(from: string, to: string) {
  return this.http.get<any[]>(
    `${this.customersUrl}/unreachable/summary-range`,
    {
      headers: this.getAuthHeaders(),
      params: { from, to },
    }
  );
}
getRejectedSummaryByRange(from: string, to: string) {
  return this.http.get(
    `${this.customersUrl}/rejected/summary-range`,
    {
      headers: this.getAuthHeaders(),
      params: { from, to },
    }
  );
}




}
